import type { WorkDetail } from '../work/WorkDetailModal';
import type { WorkSummary } from '../../../services/postService';

export interface ExampleWorkValue {
    id: string | number;
    type: 'video' | 'image';
    thumbnailUrl: string;
    videoUrl?: string;
    images?: string[];
    title: string;
    description?: string;
    sourceUrl?: string;
    localFile?: File;
    localThumbnailDataUrl?: string;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogg', 'ogv']);
const MEDIA_PROBE_TIMEOUT_MS = 5000;

export const buildServiceExampleWorkThreadId = (serviceId: string | number, exampleWorkId: string | number) => (
    `service_example:${String(serviceId)}:${String(exampleWorkId)}`
);

export const buildBriefExampleWorkThreadId = (briefId: string | number, exampleWorkId: string | number) => (
    `brief_example:${String(briefId)}:${String(exampleWorkId)}`
);

export const extractPostIdFromInput = (input: string) => {
    const directMatch = input.match(/\bpost_[a-zA-Z0-9]+\b/);
    if (directMatch) {
        return directMatch[0];
    }

    try {
        const parsed = new URL(input);
        const queryMatches = [
            parsed.searchParams.get('postId'),
            parsed.searchParams.get('id')
        ].filter((value): value is string => !!value && /^post_[a-zA-Z0-9]+$/.test(value));
        if (queryMatches.length > 0) {
            return queryMatches[0];
        }

        const pathMatch = parsed.pathname.match(/\/(?:posts?|works?)\/(post_[a-zA-Z0-9]+)/i);
        if (pathMatch) {
            return pathMatch[1];
        }
    } catch {
        return null;
    }

    return null;
};

const getYoutubeVideoId = (input: string) => {
    try {
        const parsed = new URL(input);
        const hostname = parsed.hostname.replace(/^www\./, '').toLowerCase();

        if (hostname === 'youtu.be') {
            const id = parsed.pathname.replace(/^\/+/, '').split('/')[0];
            return id || null;
        }

        if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
            if (parsed.pathname === '/watch') {
                return parsed.searchParams.get('v');
            }

            const shortsMatch = parsed.pathname.match(/^\/shorts\/([a-zA-Z0-9_-]+)/);
            if (shortsMatch) {
                return shortsMatch[1];
            }

            const embedMatch = parsed.pathname.match(/^\/embed\/([a-zA-Z0-9_-]+)/);
            if (embedMatch) {
                return embedMatch[1];
            }
        }
    } catch {
        return null;
    }

    return null;
};

const getUrlExtension = (input: string) => {
    try {
        const parsed = new URL(input);
        const lastSegment = parsed.pathname.split('/').pop() || '';
        if (!lastSegment.includes('.')) {
            return '';
        }
        return lastSegment.split('.').pop()?.toLowerCase() || '';
    } catch {
        return '';
    }
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, fallback: T): Promise<T> => (
    new Promise((resolve) => {
        let settled = false;
        const timer = window.setTimeout(() => {
            if (!settled) {
                settled = true;
                resolve(fallback);
            }
        }, timeoutMs);

        promise
            .then((value) => {
                if (!settled) {
                    settled = true;
                    window.clearTimeout(timer);
                    resolve(value);
                }
            })
            .catch(() => {
                if (!settled) {
                    settled = true;
                    window.clearTimeout(timer);
                    resolve(fallback);
                }
            });
    })
);

const probeImageUrl = (url: string) => withTimeout(new Promise<boolean>((resolve) => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = url;
}), MEDIA_PROBE_TIMEOUT_MS, false);

const inspectVideoUrl = (url: string) => withTimeout(new Promise<{ playable: boolean; thumbnailUrl?: string }>((resolve) => {
    const video = document.createElement('video');
    let finished = false;

    const finish = (result: { playable: boolean; thumbnailUrl?: string }) => {
        if (finished) {
            return;
        }
        finished = true;
        video.pause();
        video.removeAttribute('src');
        video.load();
        resolve(result);
    };

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    video.onerror = () => finish({ playable: false });
    video.onloadeddata = () => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 640;
            canvas.height = video.videoHeight || 360;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                finish({ playable: true });
                return;
            }

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            finish({
                playable: true,
                thumbnailUrl: canvas.toDataURL('image/jpeg', 0.82)
            });
        } catch {
            finish({ playable: true });
        }
    };

    video.src = url;
}), MEDIA_PROBE_TIMEOUT_MS, { playable: false });

export const resolveExternalExampleWorkPreview = async (url: string): Promise<Pick<ExampleWorkValue, 'type' | 'thumbnailUrl' | 'videoUrl' | 'images'>> => {
    const trimmedUrl = url.trim();
    const youtubeVideoId = getYoutubeVideoId(trimmedUrl);
    if (youtubeVideoId) {
        return {
            type: 'video',
            thumbnailUrl: `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`,
            videoUrl: undefined,
            images: undefined
        };
    }

    const extension = getUrlExtension(trimmedUrl);
    if (IMAGE_EXTENSIONS.has(extension)) {
        return {
            type: 'image',
            thumbnailUrl: trimmedUrl,
            images: [trimmedUrl]
        };
    }

    if (VIDEO_EXTENSIONS.has(extension)) {
        const preview = await inspectVideoUrl(trimmedUrl);
        return {
            type: 'video',
            thumbnailUrl: preview.thumbnailUrl || '',
            videoUrl: trimmedUrl,
            images: undefined
        };
    }

    if (await probeImageUrl(trimmedUrl)) {
        return {
            type: 'image',
            thumbnailUrl: trimmedUrl,
            images: [trimmedUrl]
        };
    }

    const videoPreview = await inspectVideoUrl(trimmedUrl);
    if (videoPreview.playable) {
        return {
            type: 'video',
            thumbnailUrl: videoPreview.thumbnailUrl || '',
            videoUrl: trimmedUrl,
            images: undefined
        };
    }

    return {
        type: 'image',
        thumbnailUrl: '',
        images: undefined
    };
};

const isAliyunOssUrl = (url: string) => {
    try {
        return new URL(url).hostname.includes('aliyuncs.com');
    } catch {
        return url.includes('aliyuncs.com/');
    }
};

export const normalizePersistedExampleWorkUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
    }

    if (isAliyunOssUrl(url)) {
        return url.split('?')[0];
    }

    return url;
};

export const mapWorkSummaryToExampleWork = (work: WorkSummary): ExampleWorkValue => {
    const normalizedThumbnailUrl = normalizePersistedExampleWorkUrl(work.thumbnailUrl);
    const normalizedImages = (work.images || [])
        .map(normalizePersistedExampleWorkUrl)
        .filter(Boolean);

    return {
        id: work.id,
        type: work.type,
        thumbnailUrl: normalizedThumbnailUrl,
        videoUrl: work.videoUrl ? normalizePersistedExampleWorkUrl(work.videoUrl) : undefined,
        images: work.type === 'image'
            ? (normalizedImages.length > 0 ? normalizedImages : (normalizedThumbnailUrl ? [normalizedThumbnailUrl] : []))
            : undefined,
        title: work.title,
        description: work.description || ''
    };
};

export const mapWorkDetailToExampleWork = (work: WorkDetail): ExampleWorkValue => {
    const normalizedThumbnailUrl = normalizePersistedExampleWorkUrl(work.thumbnailUrl);
    const normalizedImages = (work.images || [])
        .map(normalizePersistedExampleWorkUrl)
        .filter(Boolean);

    return {
        id: work.id,
        type: work.type,
        thumbnailUrl: normalizedThumbnailUrl,
        videoUrl: work.videoUrl ? normalizePersistedExampleWorkUrl(work.videoUrl) : undefined,
        images: work.type === 'image'
            ? (normalizedImages.length > 0 ? normalizedImages : (normalizedThumbnailUrl ? [normalizedThumbnailUrl] : []))
            : undefined,
        title: work.title,
        description: work.description || '',
        sourceUrl: work.sourceUrl
    };
};
